import { IntlShape } from 'react-intl'
import { z } from 'zod'

export const getAssetManagementValidationSchema = (formatMessage: IntlShape['formatMessage']) => {
  const positiveNumber = z
    .union([
      z
        .string()
        .trim()
        .transform((val) => (val === '' ? undefined : parseFloat(val))),
      z.number(),
    ])
    .nullable()
    .transform((val) => (val === null || isNaN(val as number) ? undefined : val))
    .refine((val) => val === undefined || val > 0, {
      message: formatMessage({
        id: 'assetsManagement.positiveNumber',
        defaultMessage: 'Must be positive',
      }),
    })

  const detail = z
    .object({
      id: z.number().optional(),
      brandId: z.number().positive().optional(),
      manufacturerId: z.number().positive().optional(),
      assetId: z.number().positive().optional(),
      model: z
        .string()
        .max(100, {
          message: formatMessage({
            id: 'assetsManagement.modelMaxLength',
            defaultMessage: 'Model must be at most 100 characters',
          }),
        })
        .optional(),
      productionYear: z
        .union([
          z
            .string()
            .trim()
            .transform((val) => {
              if (val === null || val === undefined) return undefined
              if (typeof val === 'string') {
                const strVal = val as string
                if (strVal.trim() === '') return undefined
                if (!isNaN(Number(strVal))) return Number(strVal)
              }
              return undefined
            }),
          z.number(),
          z.date().transform((val) => val.getFullYear()),
        ])
        .nullable()
        .refine(
          (val) => {
            if (!val) return true
            return val >= 1900 && val <= new Date().getFullYear()
          },
          {
            message: formatMessage({
              id: 'assetsManagement.productionYearRange',
              defaultMessage: `Production year must be between 1900 and ${new Date().getFullYear()}`,
            }),
          }
        )
        .refine(
          (val) => {
            if (!val) return true
            return val <= new Date().getFullYear()
          },
          {
            message: formatMessage({
              id: 'assetsManagement.productionYearNotFuture',
              defaultMessage: 'Production year cannot be in the future',
            }),
          }
        )
        .transform((val) => {
          if (!val) return undefined
          if (typeof val === 'number') return val
          if ((val as any) instanceof Date) return (val as Date).getFullYear()
          if (typeof val === 'string') {
            const num = Number(val)
            return isNaN(num) ? undefined : num
          }
          return undefined
        })
        .optional(),
      numberOfDoors: positiveNumber
        .refine((val) => val === undefined || (val >= 0 && val <= 255), {
          message: formatMessage({
            id: 'assetsManagement.numberOfDoorsRange',
            defaultMessage: 'Number of doors must be between 0 and 255',
          }),
        })
        .optional(),
      chassisNumber: z
        .string()
        .max(100, {
          message: formatMessage({
            id: 'assetsManagement.chassisNumberMaxLength',
            defaultMessage: 'Chassis Number must be at most 100 characters',
          }),
        })
        .optional(),
      registrationNumber: z
        .string()
        .max(20, {
          message: formatMessage({
            id: 'assetsManagement.registrationNumberMaxLength',
            defaultMessage: 'Registration Number must be at most 20 characters',
          }),
        })
        .optional(),
      registrationDate: z
        .union([z.string(), z.date()])
        .optional()
        .transform((val) => {
          if (val === undefined || val === null) return undefined
          if (val instanceof Date) return val
          if (typeof val === 'string' && val.trim() !== '') {
            const d = new Date(val)
            return isNaN(d.getTime()) ? undefined : d
          }
          return undefined
        })
        .refine(
          (val) => {
            if (!val || !(val instanceof Date)) return true
            return val <= new Date()
          },
          {
            message: formatMessage({
              id: 'assetsManagement.registrationDateNotFuture',
              defaultMessage: 'Registration Date cannot be in the future',
            }),
          }
        ),
      registrationExpiry: z
        .union([z.string(), z.date()])
        .optional()
        .transform((val) => {
          if (val === undefined || val === null) return undefined
          if (val instanceof Date) return val
          if (typeof val === 'string' && val.trim() !== '') {
            const d = new Date(val)
            return isNaN(d.getTime()) ? undefined : d
          }
          return undefined
        })
        .refine(
          (val) => {
            if (!val || !(val instanceof Date)) return true
            const now = new Date()
            now.setHours(0, 0, 0, 0)
            return val >= now
          },
          {
            message: formatMessage({
              id: 'assetsManagement.registrationExpiryNotPast',
              defaultMessage: 'Registration Expiry cannot be in the past',
            }),
          }
        ),
      insuranceExpiry: z
        .union([z.string(), z.date()])
        .optional()
        .transform((val) => {
          if (val === undefined || val === null) return undefined
          if (val instanceof Date) return val
          if (typeof val === 'string' && val.trim() !== '') {
            const d = new Date(val)
            return isNaN(d.getTime()) ? undefined : d
          }
          return undefined
        })
        .refine(
          (val) => {
            if (!val || !(val instanceof Date)) return true
            const now = new Date()
            now.setHours(0, 0, 0, 0)
            return val >= now
          },
          {
            message: formatMessage({
              id: 'assetsManagement.insuranceExpiryNotPast',
              defaultMessage: 'Insurance Expiry cannot be in the past',
            }),
          }
        ),
      operationCardExpiry: z
        .union([z.string(), z.date()])
        .optional()
        .transform((val) => {
          if (val === undefined || val === null) return undefined
          if (val instanceof Date) return val
          if (typeof val === 'string' && val.trim() !== '') {
            const d = new Date(val)
            return isNaN(d.getTime()) ? undefined : d
          }
          return undefined
        })
        .refine(
          (val) => {
            if (!val || !(val instanceof Date)) return true
            const now = new Date()
            now.setHours(0, 0, 0, 0)
            return val >= now
          },
          {
            message: formatMessage({
              id: 'assetsManagement.operationCardExpiryNotPast',
              defaultMessage: 'Operation Card Expiry cannot be in the past',
            }),
          }
        ),
      mvpiExpiry: z
        .union([z.string(), z.date()])
        .optional()
        .transform((val) => {
          if (val === undefined || val === null) return undefined
          if (val instanceof Date) return val
          if (typeof val === 'string' && val.trim() !== '') {
            const d = new Date(val)
            return isNaN(d.getTime()) ? undefined : d
          }
          return undefined
        })
        .refine(
          (val) => {
            if (!val || !(val instanceof Date)) return true
            const now = new Date()
            now.setHours(0, 0, 0, 0)
            return val >= now
          },
          {
            message: formatMessage({
              id: 'assetsManagement.mvpiExpiryNotPast',
              defaultMessage: 'MVPI Expiry cannot be in the past',
            }),
          }
        ),
      maintenanceEndDate: z
        .union([z.string(), z.date()])
        .optional()
        .transform((val) => {
          if (val === undefined || val === null) return undefined
          if (val instanceof Date) return val
          if (typeof val === 'string' && val.trim() !== '') {
            const d = new Date(val)
            return isNaN(d.getTime()) ? undefined : d
          }
          return undefined
        })
        .refine(
          (val) => {
            if (!val || !(val instanceof Date)) return true
            const now = new Date()
            now.setHours(0, 0, 0, 0)
            return val >= now
          },
          {
            message: formatMessage({
              id: 'assetsManagement.maintenanceEndDateNotPast',
              defaultMessage: 'Maintenance End Date cannot be in the past',
            }),
          }
        ),
      isOwned: z.boolean({
        required_error: formatMessage({
          id: 'assetsManagement.isOwnedRequired',
          defaultMessage: 'Is Owned is required',
        }),
      }),
      ownerName: z
        .string()
        .max(100, {
          message: formatMessage({
            id: 'assetsManagement.ownerNameMaxLength',
            defaultMessage: 'Owner Name must be at most 100 characters',
          }),
        })
        .optional(),
      isUsedForExport: z.boolean().optional(),
      exportDocumentationAvailable: z.boolean().optional(),
      exportDocumentationExpiry: z
        .union([z.string(), z.date()])
        .optional()
        .transform((val) => {
          if (val === undefined || val === null) return undefined
          if (val instanceof Date) return val
          if (typeof val === 'string' && val.trim() !== '') {
            const d = new Date(val)
            return isNaN(d.getTime()) ? undefined : d
          }
          return undefined
        }),
    })
    .superRefine((val, ctx) => {
      if (val.isUsedForExport) {
        if (typeof val.exportDocumentationAvailable !== 'boolean') {
          ctx.addIssue({
            path: ['exportDocumentationAvailable'],
            code: z.ZodIssueCode.custom,
            message: formatMessage({
              id: 'assetsManagement.exportDocumentationAvailableRequired',
              defaultMessage:
                'Documentation availability is required when asset is used for export',
            }),
          })
        }
        if (val.exportDocumentationAvailable) {
          if (!val.exportDocumentationExpiry) {
            ctx.addIssue({
              path: ['exportDocumentationExpiry'],
              code: z.ZodIssueCode.custom,
              message: formatMessage({
                id: 'assetsManagement.exportDocumentationExpiryRequired',
                defaultMessage:
                  'Documentation expiry date is required when documentation is available',
              }),
            })
          } else if (
            val.exportDocumentationExpiry instanceof Date &&
            val.exportDocumentationExpiry < new Date()
          ) {
            ctx.addIssue({
              path: ['exportDocumentationExpiry'],
              code: z.ZodIssueCode.custom,
              message: formatMessage({
                id: 'assetsManagement.exportDocumentationExpiryNotPast',
                defaultMessage: 'Documentation expiry date cannot be in the past',
              }),
            })
          }
        }
      }
    })

  const physicalProperties = z
    .object({
      id: z.number().optional(),
      weightMeasureUnitId: positiveNumber.optional(),
      volumeMeasureUnitId: positiveNumber.optional(),
      lengthMeasureUnitId: positiveNumber.optional(),
      kerbWeight: positiveNumber
        .refine((val) => val === undefined || val <= 99999999.9999, {
          message: formatMessage({
            id: 'assetsManagement.kerbWeightMax',
            defaultMessage: 'Max 99,999,999.9999',
          }),
        })
        .optional(),
      grossWeight: positiveNumber
        .refine((val) => val === undefined || val <= 99999999.9999, {
          message: formatMessage({
            id: 'assetsManagement.grossWeightMax',
            defaultMessage: 'Max 99,999,999.9999',
          }),
        })
        .optional(),
      permissibleLoad: positiveNumber
        .refine((val) => val === undefined || val <= 99999999.9999, {
          message: formatMessage({
            id: 'assetsManagement.permissibleLoadMax',
            defaultMessage: 'Max 99,999,999.9999',
          }),
        })
        .optional(),
      tareWeight: positiveNumber
        .refine((val) => val === undefined || val <= 99999999.9999, {
          message: formatMessage({
            id: 'assetsManagement.tareWeightMax',
            defaultMessage: 'Max 99,999,999.9999',
          }),
        })
        .optional(),
      chargeableWeight: positiveNumber
        .refine((val) => val === undefined || val <= 99999999.9999, {
          message: formatMessage({
            id: 'assetsManagement.chargeableWeightMax',
            defaultMessage: 'Max 99,999,999.9999',
          }),
        })
        .optional(),
      externalVolume: positiveNumber
        .refine((val) => val === undefined || val <= 99999999.9999, {
          message: formatMessage({
            id: 'assetsManagement.externalVolumeMax',
            defaultMessage: 'Max 99,999,999.9999',
          }),
        })
        .optional(),
      externalHeight: positiveNumber
        .refine((val) => val === undefined || val <= 99999999.9999, {
          message: formatMessage({
            id: 'assetsManagement.externalHeightMax',
            defaultMessage: 'Max 99,999,999.9999',
          }),
        })
        .optional(),
      externalLength: positiveNumber
        .refine((val) => val === undefined || val <= 99999999.9999, {
          message: formatMessage({
            id: 'assetsManagement.externalLengthMax',
            defaultMessage: 'Max 99,999,999.9999',
          }),
        })
        .optional(),
      externalWidth: positiveNumber
        .refine((val) => val === undefined || val <= 99999999.9999, {
          message: formatMessage({
            id: 'assetsManagement.externalWidthMax',
            defaultMessage: 'Max 99,999,999.9999',
          }),
        })
        .optional(),
      internalVolume: positiveNumber
        .refine((val) => val === undefined || val <= 99999999.9999, {
          message: formatMessage({
            id: 'assetsManagement.internalVolumeMax',
            defaultMessage: 'Max 99,999,999.9999',
          }),
        })
        .optional(),
      internalHeight: positiveNumber
        .refine((val) => val === undefined || val <= 99999999.9999, {
          message: formatMessage({
            id: 'assetsManagement.internalHeightMax',
            defaultMessage: 'Max 99,999,999.9999',
          }),
        })
        .optional(),
      internalLength: positiveNumber
        .refine((val) => val === undefined || val <= 99999999.9999, {
          message: formatMessage({
            id: 'assetsManagement.internalLengthMax',
            defaultMessage: 'Max 99,999,999.9999',
          }),
        })
        .optional(),
      internalWidth: positiveNumber
        .refine((val) => val === undefined || val <= 99999999.9999, {
          message: formatMessage({
            id: 'assetsManagement.internalWidthMax',
            defaultMessage: 'Max 99,999,999.9999',
          }),
        })
        .optional(),
      assetId: z.number().optional(),
      radius: positiveNumber
        .refine((val) => val === undefined || val <= 99999999.9999, {
          message: formatMessage({
            id: 'assetsManagement.radiusMax',
            defaultMessage: 'Max 99,999,999.9999',
          }),
        })
        .optional(),
      clearLoadingHeight: positiveNumber
        .refine((val) => val === undefined || val <= 99999999.9999, {
          message: formatMessage({
            id: 'assetsManagement.clearLoadingHeightMax',
            defaultMessage: 'Max 99,999,999.9999',
          }),
        })
        .optional(),
      useDimensions: z.boolean().optional(),
      isRectangular: z.boolean().optional(),
      internalUseDimensions: z.boolean().optional(),
      externalUseDimensions: z.boolean().optional(),
      internalIsRectangular: z.boolean().optional(),
      externalIsRectangular: z.boolean().optional(),
    })
    .superRefine((data, ctx) => {
      if (!data) return

      const hasWeightData =
        data.kerbWeight ||
        data.grossWeight ||
        data.permissibleLoad ||
        data.tareWeight ||
        data.chargeableWeight
      const hasExternalDimensions =
        data.externalHeight || data.externalLength || data.externalWidth
      const hasInternalDimensions =
        data.internalHeight || data.internalLength || data.internalWidth

      if (hasWeightData && (!data.weightMeasureUnitId || data.weightMeasureUnitId === -1)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage({
            id: 'assetsManagement.weightUnitRequired',
            defaultMessage: 'Weight unit is required when weight data is provided',
          }),
          path: ['weightMeasureUnitId'],
        })
      }

      if (hasExternalDimensions && (!data.lengthMeasureUnitId || data.lengthMeasureUnitId === -1)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage({
            id: 'assetsManagement.lengthUnitRequired',
            defaultMessage: 'Length unit is required when external dimensions are provided',
          }),
          path: ['lengthMeasureUnitId'],
        })
      }

      if (hasInternalDimensions && (!data.lengthMeasureUnitId || data.lengthMeasureUnitId === -1)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage({
            id: 'assetsManagement.lengthUnitRequired',
            defaultMessage: 'Length unit is required when internal dimensions are provided',
          }),
          path: ['lengthMeasureUnitId'],
        })
      }

      if (
        (data.externalVolume || data.internalVolume) &&
        (!data.volumeMeasureUnitId || data.volumeMeasureUnitId === -1)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage({
            id: 'assetsManagement.volumeUnitRequired',
            defaultMessage: 'Volume unit is required when volume data is provided',
          }),
          path: ['volumeMeasureUnitId'],
        })
      }

      if (data.kerbWeight && data.grossWeight && data.kerbWeight > data.grossWeight) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage({
            id: 'assetsManagement.kerbWeightCannotExceedGrossWeight',
            defaultMessage: 'Kerb weight cannot exceed gross weight',
          }),
          path: ['kerbWeight'],
        })
      }

      if (data.grossWeight && data.permissibleLoad && data.grossWeight > data.permissibleLoad) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage({
            id: 'assetsManagement.grossWeightCannotExceedPermissibleLoad',
            defaultMessage: 'Gross weight cannot exceed permissible load',
          }),
          path: ['grossWeight'],
        })
      }

      if (data.externalHeight && data.internalHeight && data.externalHeight < data.internalHeight) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage({
            id: 'assetsManagement.externalHeightCannotBeLessThanInternal',
            defaultMessage: 'External height cannot be less than internal height',
          }),
          path: ['externalHeight'],
        })
      }

      if (data.externalLength && data.internalLength && data.externalLength < data.internalLength) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage({
            id: 'assetsManagement.externalLengthCannotBeLessThanInternal',
            defaultMessage: 'External length cannot be less than internal length',
          }),
          path: ['externalLength'],
        })
      }

      if (data.externalWidth && data.internalWidth && data.externalWidth < data.internalWidth) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage({
            id: 'assetsManagement.externalWidthCannotBeLessThanInternal',
            defaultMessage: 'External width cannot be less than internal width',
          }),
          path: ['externalWidth'],
        })
      }

      if (data.externalVolume && data.internalVolume && data.externalVolume < data.internalVolume) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage({
            id: 'assetsManagement.externalVolumeCannotBeLessThanInternal',
            defaultMessage: 'External volume cannot be less than internal volume',
          }),
          path: ['externalVolume'],
        })
      }
    })

  return z
    .object({
      id: z.number().optional(),
      validFrom: z.union([z.date(), z.string()]).nullable().optional(),
      assetClassId: z
        .number({
          required_error: formatMessage({
            id: 'assetsManagement.assetClassIdRequired',
            defaultMessage: 'Asset Class is required',
          }),
        })
        .positive({
          message: formatMessage({
            id: 'assetsManagement.assetClassIdRequired',
            defaultMessage: 'Asset Class is required',
          }),
        }),
      assetTypeId: z
        .number({
          required_error: formatMessage({
            id: 'assetsManagement.assetTypeIdRequired',
            defaultMessage: 'Asset Type is required',
          }),
        })
        .positive({
          message: formatMessage({
            id: 'assetsManagement.assetTypeIdRequired',
            defaultMessage: 'Asset Type is required',
          }),
        }),
      transportModeId: z.number({
        required_error: formatMessage({
          id: 'assetsManagement.transportModeIdRequired',
          defaultMessage: 'Transport mode ID is required',
        }),
      }),
      assetCategoryId: z
        .number({
          required_error: formatMessage({
            id: 'assetsManagement.assetCategoryIdRequired',
            defaultMessage: 'Asset Category is required',
          }),
        })
        .positive({
          message: formatMessage({
            id: 'assetsManagement.assetCategoryIdRequired',
            defaultMessage: 'Asset Category is required',
          }),
        }),
      identification: z
        .string({
          required_error: formatMessage({
            id: 'assetsManagement.identificationRequired',
            defaultMessage: 'Fleet Number is required',
          }),
        })
        .max(50, {
          message: formatMessage({
            id: 'assetsManagement.identificationMaxLength',
            defaultMessage: 'Identification must be at most 50 characters',
          }),
        }),
      detail,
      physicalProperties,
      tenantId: z.number().optional(),
      documents: z.array(z.any()).optional(),
      active: z.boolean().optional(),
    })
    .superRefine((val, ctx) => {
      if (val.detail?.isUsedForExport) {
        if (!val.documents || val.documents.length === 0) {
          ctx.addIssue({
            path: ['documents'],
            code: z.ZodIssueCode.custom,
            message: formatMessage({
              id: 'assetsManagement.documentsRequiredForExport',
              defaultMessage: 'At least one document is required when asset is used for export',
            }),
          })
        }
      }
    })
}

export type AssetManagementSchemaType = z.infer<
  ReturnType<typeof getAssetManagementValidationSchema>
>
